import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * HoverButton — botão sólido com efeito de preenchimento no hover (CTAs primários)
 *
 * Quando showDot=true  → ponto visível na esquerda expande e preenche (estilo Magic UI original)
 * Quando showDot=false → círculo invisível no centro expande e preenche (sem dot visível)
 *
 * Props:
 *  children       {ReactNode}
 *  className      {string}     Classes extras para tamanho/fonte/layout
 *  bgColor        {string}     Cor de fundo do botão                     default: verde primário
 *  dotColor       {string}     Cor do preenchimento no hover              default: verde escuro
 *  textColor      {string}     Cor do texto no estado padrão             default: inherit
 *  textHoverColor {string}     Cor do texto no hover                     default: verde primário
 *  showDot        {boolean}    Exibe o ponto visível à esquerda          default: true
 *  style          {object}     Inline styles extras (boxShadow etc.)
 */
export function HoverButton({
  children,
  className = '',
  bgColor = 'var(--color-primary, #51e1a5)',
  dotColor = 'var(--color-on-primary, #003824)',
  textColor = 'inherit',
  textHoverColor = 'var(--color-primary, #51e1a5)',
  showDot = true,
  style = {},
  ...props
}) {
  return (
    <button
      className={`group relative cursor-pointer overflow-hidden rounded-xl border px-6 py-3 text-center font-semibold transition-all ${className}`}
      style={{ backgroundColor: bgColor, color: textColor, ...style }}
      {...props}
    >
      {showDot ? (
        /* Modo com dot: ponto visível que expande ao escalar */
        <div className="flex items-center justify-center gap-2">
          <div
            className="h-2 w-2 flex-shrink-0 rounded-full transition-all duration-300 group-hover:scale-[100.8]"
            style={{ backgroundColor: dotColor }}
          />
          <span className="inline-block transition-all duration-300 group-hover:translate-x-12 group-hover:opacity-0">
            {children}
          </span>
        </div>
      ) : (
        /* Modo sem dot: círculo invisível no centro expande para preencher */
        <div className="flex items-center justify-center">
          <div
            className="absolute left-1/2 top-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-500 group-hover:h-[300%] group-hover:w-[300%]"
            style={{ backgroundColor: dotColor }}
          />
          <span className="relative inline-block transition-all duration-300 group-hover:translate-x-12 group-hover:opacity-0">
            {children}
          </span>
        </div>
      )}

      {/* Hover reveal — slides in from right */}
      <div
        className="absolute top-0 z-10 flex h-full w-full translate-x-12 items-center justify-center gap-2 opacity-0 transition-all duration-300 group-hover:-translate-x-5 group-hover:opacity-100"
        style={{ color: textHoverColor }}
      >
        <span>{children}</span>
        <ArrowRight className="w-4 h-4" />
      </div>
    </button>
  );
}
